import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { COOKIE_PURPOSES, getCookieChoices, reopenCookieBanner, setCookieChoices, useCookieBannerOpen, type Choices, type Purpose } from '../analytics';
import { LEGAL_URLS } from '../config';
import { Button } from './ui';

// Asks for consent to the portal's cookies before any of them is set (src/analytics.ts), as the website does: Accept
// all, Refuse all, or Customize my choices, which shows one box per purpose, unticked until accepted. The text names
// every purpose and how to change one's mind later (CNIL recommendation of 2020, §41). Refusing everything is one
// click, as accepting everything is, and leaves the portal unchanged. Mounted once for every page (App.tsx): the tags
// run on every page once accepted, so the banner asks, and "Manage cookies" reopens it, on any of them.
export function CookieBanner() {
  const open = useCookieBannerOpen();
  // Mounted at each opening, so its boxes start from the choices then in force.
  return open ? <CookieChoices /> : null;
}

/** "Manage cookies", at the bottom of every page (the public, signed-in and checkout frames): withdrawing is as easy as
 *  accepting, wherever the tags run (privacy policy, clause 9). */
export function ManageCookies({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  if (COOKIE_PURPOSES.length === 0) return null;
  return (
    <button type="button" onClick={reopenCookieBanner} className={`self-center text-[11.5px] text-fainter underline hover:text-muted ${className}`}>
      {t('cookies.manage')}
    </button>
  );
}

function CookieChoices() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Choices>(getCookieChoices);
  // Reopened from the page's link, with every purpose already chosen: the boxes show at once.
  const [expanded, setExpanded] = useState(() => COOKIE_PURPOSES.every((p) => draft[p] !== undefined));
  const firstBox = useRef<HTMLInputElement>(null);
  // Customize gives way to Save my choices: the focus goes to the boxes rather than being lost.
  useEffect(() => {
    if (expanded) firstBox.current?.focus();
  }, [expanded]);
  const save = (granted: (p: Purpose) => boolean) =>
    setCookieChoices(Object.fromEntries(COOKIE_PURPOSES.map((p) => [p, granted(p)])));
  const purposes = COOKIE_PURPOSES.map((p) => t(`cookies.short.${p}`)).join(t('cookies.and'));
  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookies.title')}
      className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-[520px] flex-col gap-3 rounded-lg border border-black/10 bg-pane p-4 text-[13px] leading-[1.5] text-ink shadow-lg"
    >
      <p>
        <Trans
          t={t}
          i18nKey="cookies.body"
          values={{ purposes }}
          components={{ privacy: <a href={LEGAL_URLS.privacy} target="_blank" rel="noreferrer" className="text-accent underline hover:text-accent-dark" /> }}
        />
      </p>
      {expanded && (
        <ul className="flex flex-col gap-2">
          {COOKIE_PURPOSES.map((p, i) => (
            <li key={p}>
              <label className="flex items-start gap-2">
                <input
                  ref={i === 0 ? firstBox : undefined}
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(draft[p])}
                  onChange={(e) => setDraft({ ...draft, [p]: e.target.checked })}
                />
                <span>
                  <strong>{t(`cookies.purposes.${p}.title`)}</strong> {t(`cookies.purposes.${p}.text`)}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {expanded ? (
          <Button type="button" variant="secondary" onClick={() => save((p) => Boolean(draft[p]))}>
            {t('cookies.save')}
          </Button>
        ) : (
          <button type="button" className="mr-auto text-accent underline hover:text-accent-dark" onClick={() => setExpanded(true)}>
            {t('cookies.customize')}
          </button>
        )}
        <Button type="button" variant="secondary" onClick={() => save(() => false)}>
          {t('cookies.refuse')}
        </Button>
        <Button type="button" onClick={() => save(() => true)}>
          {t('cookies.accept')}
        </Button>
      </div>
    </div>
  );
}
